"use client";

// frontend/packages/business-console/src/components/help/HelpPage.jsx
//
// In-app help page with searchable articles. Linked from the Console
// header nav and from contextual HelpLink components throughout the editor.

import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { HELP_TOPICS } from "./HelpLink";

export default function HelpPage() {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  // Honor the deep-link hash (HelpLink uses /help#<topic-key>): expand the
  // matching article on mount and scroll it into view.
  useEffect(() => {
    const hash = typeof window !== "undefined" ? window.location.hash.slice(1) : "";
    if (!hash || !HELP_TOPICS[hash]) return;

    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(hash);
      return next;
    });

    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const topics = Object.entries(HELP_TOPICS);
  const isSearching = search.trim().length > 0;
  const filtered = isSearching
    ? topics.filter(([, article]) => {
        const q = search.toLowerCase();
        return (
          article.title.toLowerCase().includes(q) ||
          article.content.some((line) => line.toLowerCase().includes(q))
        );
      })
    : topics;

  const handleToggle = (key) => (_, isExpanded) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isExpanded) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
        Help Center
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
        Quick answers for common tasks. For anything not covered here, contact the UpRyzze team.
      </Typography>

      <TextField
        placeholder="Search help articles..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 3 }}
      />

      {filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            No articles match your search. Try different keywords, or contact the UpRyzze team for help.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {filtered.map(([key, article]) => (
            <Accordion
              key={key}
              id={key}
              variant="outlined"
              expanded={isSearching ? true : expanded.has(key)}
              onChange={handleToggle(key)}
              disableGutters
              sx={{ "&:before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {article.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
                  {article.content.map((step, i) => (
                    <Typography
                      key={i}
                      component="li"
                      variant="body2"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {step}
                    </Typography>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{ p: 3, mt: 4, textAlign: "center", background: "rgba(52,152,219,0.04)" }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
          Still need help?
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Contact the UpRyzze team and we will take care of it for you.
        </Typography>
      </Paper>
    </Box>
  );
}
